import { Entity } from '@toggle/toggle';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { NewsFilter } from '~/services/news/news.types';
import { useNewsFilters } from '~/services/news/news-service';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useWatchlist } from '~/stores/use-watchlist/useWatchlist';
import { useNewsUrlStore } from '~/views/news/stores/use-news-url-store/useNewsUrlStore';
import {
  applySelectionMapping,
  countFilterKeyChecked,
  createAccordionFilters,
  toggleSelectionMapping,
} from '~/views/news/utils/news-filters-utils/news-filters-utils';

import { ClearFiltersButton } from '../clear-filters-button/ClearFiltersButton';
import { HighlightsFilters } from '../highlights-filters/HighlightsFilters';
import { NewsList } from '../news-list/NewsList';
import { SideAccordionFilters } from '../side-accordion-filters/SideAccordionFilters';
import * as S from './NewsView.styles';

export interface NewsViewProps {
  entity?: Entity;
  includedFilterKeys?: NewsFilter['key'][];
}

export const NewsView: FC<NewsViewProps> = ({ entity, includedFilterKeys }) => {
  const { t } = useTranslation('news');
  const { lists } = useWatchlist();
  const { data: serverFilters = [] } = useNewsFilters();

  const { trackNewsFilter } = usePosthogEvents();
  const {
    newsMostRead,
    aOMostReadNews,
    filtersMappingFromUrl,
    setMostReadParam,
    setAOMostReadNews,
    setUrlFilters,
    resetAll,
  } = useNewsUrlStore(s => ({
    newsMostRead: s.newsMostRead === '1',
    aOMostReadNews: s.aOMostReadNews === '1',
    filtersMappingFromUrl: s.newsFilters,
    setAOMostReadNews: s.setAOMostReadNews,
    setMostReadParam: s.setMostReadParam,
    setUrlFilters: s.setUrlFilters,
    resetAll: s.resetAll,
  }));

  const baseFilters = createAccordionFilters(serverFilters, lists);
  const filters = applySelectionMapping(
    baseFilters,
    filtersMappingFromUrl || {}
  );

  const newsFilters = includedFilterKeys
    ? filters.filter(f => includedFilterKeys.includes(f.key))
    : filters;

  const initialDataFilters = entity
    ? [
      {
        key: 'entities',
        name: 'Entity',
        options: [
          {
            isChecked: true,
            visible: true,
            key: entity.tag,
            name: entity.name,
          },
        ],
      },
    ]
    : [];
  const newsListFilters = [...initialDataFilters, ...newsFilters];

  const showResetButton =
    !newsMostRead ||
    newsFilters.some(
      f => countFilterKeyChecked({ filters, filterKey: f.key }).some
    );

  return (
    <>
      {entity && (
        <S.ListTitle>
          {entity.name} {t('news:title')}
        </S.ListTitle>
      )}
      <S.NewsViewRoot data-testid="news-lists-container">
        <S.LeftContent>
          <NewsList
            filters={newsListFilters}
            isFetchMostRead={entity ? aOMostReadNews : newsMostRead}
          />
        </S.LeftContent>
        <S.RightContent>
          <HighlightsFilters
            isFetchMostRead={entity ? aOMostReadNews : newsMostRead}
            toggleIsFetchMostRead={isToggled => {
              if (isToggled) {
                trackNewsFilter('news', 'most_read');
              }

              entity ? setAOMostReadNews(isToggled) : setMostReadParam(isToggled);
            }}
          />
          <SideAccordionFilters
            filters={newsFilters}
            toggleFilterOption={(filterKey, optionKey, track) => {
              track?.();
              setUrlFilters(
                toggleSelectionMapping({
                  current: filtersMappingFromUrl || {},
                  filters: newsFilters,
                  filterKey,
                  optionKey,
                })
              );
            }}
          />
          {showResetButton && (
            <ClearFiltersButton
              data-testid="reset-all-filters"
              onClick={resetAll}
            />
          )}
        </S.RightContent>
      </S.NewsViewRoot>
    </>
  );
};
